import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { useSession } from "../api/hooks.js";
import { ThemeProvider } from "../design-system/theme-provider.js";
import { RequireSession } from "../features/auth/require-session.js";
import { SignInPage } from "../features/auth/sign-in-page.js";
import { ConsolePage } from "../features/feeds/console-page.js";
import { FeedDetailLayout } from "../features/feeds/feed-detail-layout.js";
import { FeedOutputsTab } from "../features/feeds/feed-outputs-tab.js";
import { FeedOverviewTab } from "../features/feeds/feed-overview-tab.js";
import { FeedSettingsTab } from "../features/feeds/feed-settings-tab.js";
import { FeedPage } from "../features/feeds/feed-page.js";
import { FeedsPage } from "../features/feeds/feeds-page.js";
import { LibraryPage } from "../features/library/library-page.js";
import { PlaylistPage } from "../features/programming/playlist-page.js";
import { PlaylistsPage } from "../features/programming/playlists-page.js";
import { SchedulePage } from "../features/programming/schedule-page.js";
import { SchedulesPage } from "../features/programming/schedules-page.js";
import { SettingsPage } from "../features/settings/settings-page.js";
import { PlayerProvider } from "../player/player-provider.js";
import { Shell } from "./shell.js";

/**
 * One address space, two depths.
 *
 * `/feeds` and `/feeds/{id}` are the same addresses for everyone. Without a session
 * they are the listening surface, exactly as they were; with one they are the
 * console and each feed's own operating surface, which carries the listening surface
 * inside it — the operator can still play what he is looking at. There is no mode to
 * switch and no second tree of URLs for the same objects.
 */
export function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <PlayerProvider>
          <Routes>
            <Route element={<SignInPage />} path="/sign-in" />
            <Route element={<ShellLayout />}>
              <Route element={<Navigate replace to="/feeds" />} path="/" />
              <Route element={<FeedsRoute />} path="/feeds" />
              <Route element={<FeedDetailRoute />} path="/feeds/:feedId">
                <Route element={<FeedOverviewTab />} index />
                <Route element={<FeedOutputsTab />} path="outputs" />
                <Route element={<FeedSettingsTab />} path="settings" />
              </Route>
              <Route
                element={
                  <RequireSession>
                    <LibraryPage />
                  </RequireSession>
                }
                path="/library"
              />
              <Route
                element={
                  <RequireSession>
                    <PlaylistsPage />
                  </RequireSession>
                }
                path="/playlists"
              />
              <Route
                element={
                  <RequireSession>
                    <PlaylistPage />
                  </RequireSession>
                }
                path="/playlists/:playlistId"
              />
              <Route
                element={
                  <RequireSession>
                    <SchedulesPage />
                  </RequireSession>
                }
                path="/schedules"
              />
              <Route
                element={
                  <RequireSession>
                    <SchedulePage />
                  </RequireSession>
                }
                path="/schedules/:scheduleId"
              />
              <Route element={<SettingsPage />} path="/settings" />
              <Route element={<Navigate replace to="/feeds" />} path="*" />
            </Route>
          </Routes>
        </PlayerProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function ShellLayout() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

function FeedsRoute() {
  return useSession() ? <ConsolePage /> : <FeedsPage />;
}

/**
 * A feed's own address. Anonymously it is the listening page it has always been; the
 * configuration faces beneath it need a session and bounce to sign-in carrying where
 * they were.
 */
function FeedDetailRoute() {
  const session = useSession();
  const { feedId = "" } = useParams();
  const location = useLocation();
  const isIndex = location.pathname.replace(/\/$/, "") === `/feeds/${feedId}`;

  if (!session) {
    return isIndex ? (
      <FeedPage />
    ) : (
      <RequireSession>
        <Outlet />
      </RequireSession>
    );
  }
  return <FeedDetailLayout />;
}
